import React, { useState, useEffect } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  Search,
  Clock,
  Truck,
  AlertCircle,
  Store,
  CreditCard,
  Banknote
} from 'lucide-react';
import {
  getRestaurantOrders,
  updateOrderStatus,
  formatCurrency,
  handleRestaurantApiError
} from '../../../services/restaurantApi';
import ConfirmStatusChangeModal from '../modals/ConfirmStatusChangeModal';
import { toastSuccess, toastError } from '../../../utils/toast';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const TABS = [
  { id: 'live', label: 'En cours', statuses: ['pending', 'preparing'] },
  { id: 'ready', label: 'Prêtes', statuses: ['ready'] },
  { id: 'done', label: 'Terminées', statuses: ['delivered', 'cancelled'] },
];

const STATUS_BADGE = {
  pending: { bg: '#F4F1E2', fg: '#393A30', border: null, label: 'En attente' },
  preparing: { bg: '#FEF0EA', fg: '#A32C0F', border: null, label: 'En préparation' },
  ready: { bg: '#DDEECB', fg: '#054625', border: null, label: 'Prête' },
  delivered: { bg: 'transparent', fg: '#051004', border: '1px solid rgba(5,16,4,0.2)', label: 'Livrée' },
  cancelled: { bg: '#FEF0EA', fg: '#A32C0F', border: null, label: 'Annulée' },
};

const TIMELINE_STEPS = [
  { key: 'pending', label: 'Commande reçue' },
  { key: 'preparing', label: 'En cuisson' },
  { key: 'ready', label: 'Prête' },
  { key: 'delivered', label: 'Remise au client' },
];

const NewRestaurantOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('live');
  const [selectedId, setSelectedId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, order: null, newStatus: null, oldStatus: null });
  const [actionNote, setActionNote] = useState('');

  useEffect(() => {
    const loadOrders = async () => {
      setLoading(true);
      setError(null);

      try {
        const ordersData = await getRestaurantOrders();
        const ordersList = Array.isArray(ordersData) ? ordersData : (ordersData.orders || []);
        setOrders(ordersList);
        setLoading(false);

        const interval = setInterval(async () => {
          try {
            const refreshedOrders = await getRestaurantOrders();
            const refreshedList = Array.isArray(refreshedOrders) ? refreshedOrders : (refreshedOrders.orders || []);
            setOrders(refreshedList);
          } catch (err) {
            console.error('Erreur lors du rafraîchissement des commandes:', err);
          }
        }, 30000);

        return () => clearInterval(interval);
      } catch (error) {
        console.error('Erreur lors du chargement des commandes:', error);
        setError(handleRestaurantApiError(error));
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      setOrders((prev) => prev.map((order) => (order.id === orderId ? { ...order, status: newStatus } : order)));
      setActionNote(`#${orderId} · statut mis à jour ✓`);
      toastSuccess('Statut de la commande mis à jour.');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      const msg = handleRestaurantApiError(error);
      setError(msg);
      toastError(msg);
      throw error;
    }
  };

  const getStatusLabel = (status) => STATUS_BADGE[status]?.label || status || 'Inconnu';

  const getDeliveryTypeLabel = (order) => {
    const type = (order.order_type || order.orderType || '').toLowerCase();
    if (type === 'pickup' || type === 'retrait') {
      return { label: 'Retrait', Icon: Store };
    }
    return { label: 'Livraison', Icon: Truck };
  };

  const getPaymentLabel = (order) => {
    const method = (order.payment_method || order.paymentMethod || '').toLowerCase();
    const status = (order.payment_status || '').toLowerCase();
    const isCash =
      method === 'cash' || method === 'cash_on_delivery' || method === 'especes' || status === 'cash_on_delivery';
    const isPickupCash = (order.order_type === 'pickup' || order.orderType === 'pickup') && !method;
    if (isCash || isPickupCash) return { label: 'Espèces', Icon: Banknote };
    return { label: 'En ligne', Icon: CreditCard };
  };

  const formatSlot = (order) => {
    const raw = order?.created_at || order?.orderTime;
    if (!raw) return '—';
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw);
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '—';
    }
  };

  const itemsSummary = (order) => {
    const items = order?.items;
    const count = order?.items_count ?? (Array.isArray(items) ? items.length : 0);
    if (!count) return 'Aucun article';
    return `${count} plat${count > 1 ? 's' : ''}`;
  };

  const nextAction = (order) => {
    if (order.status === 'pending') return { label: 'Démarrer la cuisson', next: 'preparing' };
    if (order.status === 'preparing') return { label: 'Marquer prête', next: 'ready' };
    if (order.status === 'ready') return { label: 'Remettre au client', next: 'delivered' };
    return null;
  };

  const askCancel = (order) => {
    setConfirmModal({ isOpen: true, order, newStatus: 'cancelled', oldStatus: order.status });
  };

  const confirmStatusChange = async () => {
    if (!confirmModal.order || !confirmModal.newStatus) return;
    try {
      await handleStatusUpdate(confirmModal.order.id, confirmModal.newStatus);
      setConfirmModal({ isOpen: false, order: null, newStatus: null, oldStatus: null });
    } catch {
      setConfirmModal({ isOpen: false, order: null, newStatus: null, oldStatus: null });
    }
  };

  if (loading) return <PageSkeleton />;

  const currentTab = TABS.find((t) => t.id === tab) || TABS[0];
  const term = searchTerm.trim().toLowerCase();
  const tabOrders = orders
    .filter((o) => currentTab.statuses.includes(o.status))
    .filter((o) => {
      if (!term) return true;
      const num = String(o.orderNumber || o.id || '').toLowerCase();
      const name = (o.customer?.name || o.phone || '').toLowerCase();
      return num.includes(term) || name.includes(term);
    })
    .sort((a, b) => new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime());

  const toDoCount = orders.filter((o) => o.status === 'pending').length;
  const cookingCount = orders.filter((o) => o.status === 'preparing').length;
  const readyCount = orders.filter((o) => o.status === 'ready').length;

  const selected = tabOrders.find((o) => o.id === selectedId) || tabOrders[0] || null;
  const selectedDelivery = selected ? getDeliveryTypeLabel(selected) : null;
  const selectedPayment = selected ? getPaymentLabel(selected) : null;
  const selectedAction = selected ? nextAction(selected) : null;
  const currentStepIndex = selected ? TIMELINE_STEPS.findIndex((s) => s.key === selected.status) : -1;

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      {/* Header */}
      <div className="flex items-end gap-4 flex-wrap">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Commandes</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', background: '#FCFBF5', borderRadius: '999px', padding: '4px' }}>
          {TABS.map((t) => {
            const active = tab === t.id;
            const count = t.id === 'live' ? toDoCount + cookingCount : t.id === 'ready' ? readyCount : orders.filter((o) => t.statuses.includes(o.status)).length;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => { setTab(t.id); setSelectedId(null); }}
                style={{
                  border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13.5px',
                  padding: '9px 18px', borderRadius: '999px', whiteSpace: 'nowrap',
                  background: active ? PRIMARY : 'transparent', color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
                }}
              >
                {t.label} · {count}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <AlertCircle size={18} />
          {error?.message || error?.toString() || 'Une erreur est survenue'}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 'clamp(12px, 1.6vw, 18px)' }}>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>À préparer</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em' }}>{toDoCount}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>En cuisson</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em', color: TERRA }}>{cookingCount}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Prêtes</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em', color: PRIMARY }}>{readyCount}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Total du jour</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em' }}>{orders.length}</strong>
        </div>
      </div>

      {/* Recherche */}
      <label className="flex items-center gap-2.5" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '0 20px', minHeight: '46px' }}>
        <Search size={16} style={{ color: PRIMARY, flexShrink: 0 }} />
        <input
          type="text"
          placeholder="N° de commande, client…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '14.5px', color: '#051004', outline: 'none' }}
        />
      </label>

      {/* Liste + détail */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 'clamp(14px, 1.8vw, 22px)', alignItems: 'start' }}>

        {/* Liste */}
        <section style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(18px, 2.2vw, 26px)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>{currentTab.label}</h2>
            <span style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.64)' }}>{tabOrders.length} commande{tabOrders.length > 1 ? 's' : ''}</span>
          </div>

          {tabOrders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 16px' }}>
              <Clock size={40} style={{ color: 'rgba(5,16,4,0.18)', margin: '0 auto 14px' }} />
              <h3 style={{ margin: '0 0 6px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>Aucune commande ici</h3>
              <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}>
                {searchTerm ? 'Aucune commande ne correspond à votre recherche.' : 'Rien à afficher pour cet onglet.'}
              </p>
            </div>
          ) : (
            tabOrders.map((order) => {
              const badge = STATUS_BADGE[order.status] || STATUS_BADGE.pending;
              const isSelected = selected && order.id === selected.id;
              return (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => setSelectedId(order.id)}
                  style={{
                    cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '7px', borderRadius: '24px',
                    padding: '16px 18px', textAlign: 'left', fontFamily: 'Poppins, sans-serif',
                    border: isSelected ? `2px solid ${TERRA}` : '1px solid rgba(5,16,4,0.1)',
                    background: isSelected ? '#FBF9D9' : 'transparent',
                  }}
                >
                  <span className="flex items-center gap-2.5" style={{ width: '100%' }}>
                    <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '18px' }}>{order.orderNumber || `#${order.id}`}</strong>
                    <span style={{ background: badge.bg, color: badge.fg, border: badge.border || 'none', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '5px 12px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                      {getStatusLabel(order.status)}
                    </span>
                    <span style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.64)', whiteSpace: 'nowrap' }}>{formatSlot(order)}</span>
                  </span>
                  <span className="flex items-center gap-2.5" style={{ width: '100%', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.68)' }}>
                    {order.customer?.name || order.phone || 'Client'} · {itemsSummary(order)}
                    <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#051004' }}>{formatCurrency(order.total || 0)}</span>
                  </span>
                </button>
              );
            })
          )}
        </section>

        {/* Détail */}
        {selected && (
          <section style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '18px', position: 'sticky', top: '84px' }}>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px', letterSpacing: '-0.025em' }}>{selected.orderNumber || `#${selected.id}`}</h2>
              <span style={{ background: STATUS_BADGE[selected.status]?.bg, color: STATUS_BADGE[selected.status]?.fg, border: STATUS_BADGE[selected.status]?.border || 'none', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '5px 12px', borderRadius: '999px' }}>
                {getStatusLabel(selected.status)}
              </span>
              <span style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.64)', whiteSpace: 'nowrap' }}>{formatSlot(selected)}</span>
            </div>

            {/* Client */}
            <div className="flex items-center gap-3" style={{ background: '#FBF9D9', borderRadius: '22px', padding: '14px 16px' }}>
              <span style={{ flex: 'none', width: '40px', height: '40px', borderRadius: '999px', background: '#72B744', display: 'grid', placeItems: 'center', fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 700, color: '#051004' }}>
                {(selected.customer?.name || 'CL').trim().slice(0, 2).toUpperCase()}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                <strong style={{ fontSize: '14.5px' }}>{selected.customer?.name || 'Client'}</strong>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.66)' }}>
                  {selectedDelivery.label} · {selectedPayment.label} · {selected.customer?.phone || selected.phone || '—'}
                </span>
              </span>
            </div>

            {/* Contenu */}
            {Array.isArray(selected.items) && selected.items.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Contenu</span>
                {selected.items.map((item, i) => (
                  <span key={item.id || i} className="flex items-center gap-3">
                    <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 }}>
                      <strong style={{ fontSize: '14.5px' }}>{item.quantity ? `${item.quantity} × ` : ''}{item.name}</strong>
                    </span>
                    <strong style={{ fontSize: '14.5px', whiteSpace: 'nowrap' }}>{formatCurrency(typeof item.price === 'number' ? item.price : 0)}</strong>
                  </span>
                ))}
              </div>
            )}

            {(selected.notes || selected.special_instructions) && (
              <span style={{ background: '#FEF0EA', borderRadius: '20px', padding: '13px 16px', fontSize: '13.5px', lineHeight: 1.5, color: '#7A200B' }}>
                <strong>Note du client :</strong> {selected.notes || selected.special_instructions}
              </span>
            )}

            {/* Suivi */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '16px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Suivi</span>
              {selected.status === 'cancelled' ? (
                <span className="flex items-center gap-3" style={{ fontSize: '13.5px', fontWeight: 600, color: '#A32C0F' }}>
                  <span style={{ flex: 'none', width: '12px', height: '12px', borderRadius: '999px', background: '#A32C0F' }} />
                  Commande annulée
                </span>
              ) : (
                TIMELINE_STEPS.map((step, i) => {
                  const done = currentStepIndex >= 0 && i < currentStepIndex;
                  const current = i === currentStepIndex;
                  const future = currentStepIndex >= 0 && i > currentStepIndex;
                  return (
                    <span key={step.key} className="flex items-center gap-3" style={{ fontSize: '13.5px', fontWeight: current ? 600 : 400, color: future ? 'rgba(5,16,4,0.56)' : '#051004' }}>
                      <span style={{
                        flex: 'none', width: '12px', height: '12px', borderRadius: '999px',
                        background: done || current ? (current ? TERRA : '#72B744') : 'transparent',
                        border: future ? '2px solid rgba(5,16,4,0.26)' : 'none',
                      }} />
                      {step.label}
                      {i === 0 && selected.created_at && (
                        <span style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.6)' }}>{formatSlot(selected)}</span>
                      )}
                    </span>
                  );
                })
              )}
            </div>

            {/* Total */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '16px' }}>
              <span className="flex items-baseline gap-3">
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13.5px' }}>Total commande</span>
                <strong style={{ marginLeft: 'auto', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px' }}>{formatCurrency(selected.total || 0)}</strong>
              </span>
            </div>

            {/* Actions */}
            {selected.status !== 'delivered' && selected.status !== 'cancelled' && (
              <div className="flex gap-2.5 flex-wrap">
                {selectedAction && (
                  <button
                    type="button"
                    onClick={() => handleStatusUpdate(selected.id, selectedAction.next)}
                    style={{ flex: '1 1 170px', border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', minHeight: '50px', borderRadius: '999px', whiteSpace: 'nowrap' }}
                  >
                    {selectedAction.label}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => askCancel(selected)}
                  style={{ border: '1px solid rgba(163,44,15,0.3)', cursor: 'pointer', background: 'transparent', color: '#A32C0F', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', minHeight: '50px', padding: '0 20px', borderRadius: '999px', whiteSpace: 'nowrap' }}
                >
                  Annuler
                </button>
              </div>
            )}
            {actionNote && (
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: PRIMARY }}>{actionNote}</span>
            )}
          </section>
        )}
      </div>

      {/* Confirm Status Change Modal (annulation uniquement) */}
      <ConfirmStatusChangeModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, order: null, newStatus: null, oldStatus: null })}
        onConfirm={confirmStatusChange}
        order={confirmModal.order}
        newStatus={confirmModal.newStatus}
        oldStatus={confirmModal.oldStatus}
      />
    </div>
  );
};

export default NewRestaurantOrders;
